<template>
  <div>
    <SoPageHead :title="$t('securone.nav.groups')" :subtitle="$t('securone.groups.subtitle')">
      <select v-if="showDepartmentFilter" v-model="departmentId" class="sd-select sd-select--sm">
        <option value="">{{ $t("securone.department.filter_all") }}</option>
        <option v-for="d in departments" :key="d._id" :value="d._id">{{ d.name }}</option>
      </select>
      <SoSearch v-model="search" :placeholder="$t('securone.groups.search')" />
      <router-link v-if="canWrite.group" :to="{ name: 'securone_group_new' }" class="sd-btn sd-btn--success">{{ $t("securone.groups.new") }}<BaseIcon icon="plus" class="ml-2" /></router-link>
    </SoPageHead>

    <SoNote v-if="!canWrite.group" tone="info" class="mb-4">{{ $t("securone.groups.readonly_note") }}</SoNote>

    <div class="sd-card overflow-hidden mb-4">
      <div class="overflow-x-auto">
        <table class="sd-table sd-table--compact w-full text-sm">
          <thead>
            <tr>
              <th class="text-left">{{ $t("securone.groups.group") }}</th>
              <th class="text-left">{{ $t("securone.groups.scope") }}</th>
              <th class="text-left">{{ $t("securone.groups.linked_categories") }}</th>
              <th class="text-left"></th>
              <th v-if="anyEditable"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="g in filtered" :key="g._id" class="border-b border-secondary-100 last:border-0" :class="{ 'cursor-pointer': g.canEdit }" @click="g.canEdit && edit(g)">
              <td class="px-4 py-3">
                <div class="font-semibold text-gray-800">{{ g.name }}<SoChip v-if="g.isMember" tone="role" class="ml-2">{{ $t("securone.groups.you_are_member") }}</SoChip></div>
                <!-- T15: the member count includes SMS contacts -->
                <div class="text-xs text-gray-500 mt-0.5">{{ $tc("securone.common.members_count", g.memberCount, { n: g.memberCount }) }}<template v-if="g.smsCount"> · {{ $tc("securone.common.sms_contacts_count", g.smsCount, { n: g.smsCount }) }}</template></div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-600">{{ scopeLabel(g) }}</td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-1">
                  <SoChip v-for="c in g.linkedCategories" :key="c._id">{{ c.name }}</SoChip>
                  <span v-if="!g.linkedCategories.length" class="text-xs text-gray-400">{{ $t("securone.groups.not_linked") }}</span>
                </div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{{ $tc("securone.groups.grants", g.linkedCategories.length, { n: g.linkedCategories.length }) }}</td>
              <td v-if="anyEditable" class="px-4 py-3 text-right"><button v-if="g.canEdit" class="sd-btn sd-btn--secondary sd-btn--sm" @click.stop="edit(g)">{{ $t("edit") }}<BaseIcon icon="pencil-alt" class="ml-1" /></button></td>
            </tr>
            <tr v-if="!filtered.length"><td colspan="5" class="px-4 py-8 text-center text-gray-400">{{ $t("securone.groups.none") }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <SoNote tone="muted">{{ $t("securone.groups.rename_note") }}</SoNote>
  </div>
</template>

<script>
import SoPageHead from "./components/SoPageHead.vue";
import SoSearch from "./components/SoSearch.vue";
import SoChip from "./components/SoChip.vue";
import SoNote from "./components/SoNote.vue";
import api from "./securone.api";
import { scopeLabel } from "./securone.helpers";

// D-32. Scope shown per row (global, or the department names). Linked
// categories are read-only on the group: links live on the category (I1).
export default {
  name: "SecurOneGroups",
  components: { SoPageHead, SoSearch, SoChip, SoNote },
  data() {
    return { groups: [], search: "", departmentId: this.$store.getters["securone/departmentFilter"] || "" };
  },
  computed: {
    canWrite() {
      return this.$store.getters["securone/canWrite"];
    },
    departments() {
      return this.$store.getters["securone/departments"];
    },
    showDepartmentFilter() {
      return this.$store.getters["securone/showDepartmentFilter"];
    },
    adminVersion() {
      return this.$store.state.securone.adminVersion;
    },
    anyEditable() {
      return this.groups.some((g) => g.canEdit);
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.groups;
      return this.groups.filter((g) => [g.name, ...g.departmentNames, ...g.linkedCategories.map((c) => c.name)].join(" ").toLowerCase().includes(q));
    },
  },
  methods: {
    scopeLabel(g) {
      return scopeLabel(this, g.scope, g.departmentNames);
    },
    async load() {
      try {
        this.groups = await api.groups({ departmentId: this.departmentId || undefined });
      } catch (error) {
        this.handleError(error);
      }
    },
    edit(g) {
      this.$router.push({ name: "securone_group_edit", params: { id: g._id } });
    },
  },
  watch: {
    adminVersion() {
      this.load();
    },
    departmentId(value) {
      this.$store.commit("securone/SET_DEPARTMENT_FILTER", value || null);
      this.load();
    },
  },
  created() {
    this.setPageTitle("SecurOne · " + this.$t("securone.nav.groups"), "securone_groups");
    this.load();
  },
};
</script>
